<template>
  <Modal v-model="dialogShow" :can-close="false">
    <!-- scrollable keeps the name field and the footer buttons reachable with many options -->
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <button
          type="button"
          class="modal-close-button"
          :aria-label="$t('publication.editor.adjustments.close')"
          @click="close">
          <IconX :size="20" />
        </button>
        <div class="modal-body">
          <h1 class="mb-1">{{ $t('publication.editor.adjustments.save_title') }}</h1>
          <p class="text-muted mb-4">{{ $t('publication.editor.adjustments.save_description') }}</p>

          <LeadInput
            class="mb-4"
            :label="$t('publication.editor.adjustments.name_label')"
            :placeholder="defaultName"
            v-model="name">
            <template #hint>{{ $t('publication.editor.adjustments.name_optional_hint') }}</template>
          </LeadInput>

          <div class="adjustment-option-list-header">
            <LeadCheckbox
              :model-value="allSelected"
              :indeterminate="anySelected && !allSelected"
              :label="
                allSelected
                  ? $t('publication.editor.adjustments.deselect_all')
                  : $t('publication.editor.adjustments.select_all')
              "
              @update:model-value="setAllSelected" />
          </div>

          <div class="adjustment-option-list">
            <div v-for="item in persistableItems" :key="item.key" class="adjustment-option-row">
              <div class="adjustment-option-info">
                <div class="adjustment-option-label">{{ item.label }}</div>
                <div class="adjustment-option-value">
                  <AdjustmentValuePreview :def="item.def" :value="item.value" />
                </div>
              </div>
              <LeadButtonGroup
                class="adjustment-option-toggle"
                type="radio"
                :options="persistOptions"
                :modelValue="choices[item.key]"
                @update:modelValue="(value) => (choices[item.key] = value as PersistChoice)" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <LeadButton variant="white" :disabled="isSaving" @click="skip">
            {{ $t('publication.editor.adjustments.continue_without_saving') }}
          </LeadButton>
          <LeadButton variant="primary" :disabled="!anySelected" :loading="isSaving" @click="save">
            {{ $t('publication.editor.adjustments.save_and_continue') }}
          </LeadButton>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Modal } from '@prospective/lithium';
import { IconX } from '@tabler/icons-vue';
import LeadButtonGroup from '@/components/form/LeadButtonGroup.vue';
import LeadCheckbox from '@/components/form/LeadCheckbox.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import AdjustmentValuePreview from './AdjustmentValuePreview.vue';
import translator from '@/locale/translator';
import type { TemplateOptionDef } from '@/types/templateMeta';
import { hasValue } from './adjustmentHelpers';

type PersistChoice = 'all' | 'once';

const props = defineProps<{
  // persistable option definitions (already filtered to properties.persisted === true)
  optionDefs: TemplateOptionDef[];
  values: Record<string, unknown>;
  // name used when the user leaves the name field empty
  defaultName: string;
}>();

const dialogShow = defineModel<boolean>('dialogShow', { default: false });

const emit = defineEmits<{
  // values = only the option keys the user chose to persist for all future publications
  save: [payload: { name: string; values: Record<string, unknown> }, stopLoading: () => void];
  skip: [];
  // dismissed via the X button — no decision was made, unlike save/skip
  close: [];
}>();

/* --------------------------------- STATE ---------------------------------- */
const name = ref('');
const choices = ref<Record<string, PersistChoice>>({});
const isSaving = ref(false);

const persistOptions = computed(() => [
  { value: 'all', label: translator.get('publication.editor.adjustments.persist_all') },
  { value: 'once', label: translator.get('publication.editor.adjustments.persist_once') },
]);

// only persistable options that actually carry a value are worth offering
const persistableItems = computed(() =>
  props.optionDefs
    .filter((def) => def.properties.key && hasValue(props.values[def.properties.key!]))
    .map((def) => ({
      key: def.properties.key!,
      label: def.properties.label || def.properties.key!,
      def,
      value: props.values[def.properties.key!],
    }))
);

const anySelected = computed(() => Object.values(choices.value).some((choice) => choice === 'all'));

const allSelected = computed(
  () => persistableItems.value.length > 0 && persistableItems.value.every((item) => choices.value[item.key] === 'all')
);

/* -------------------------------- WATCHERS -------------------------------- */
// reset the per-option choices (default "only this time") whenever the dialog opens
watch(dialogShow, (open) => {
  if (open) {
    choices.value = Object.fromEntries(persistableItems.value.map((item) => [item.key, 'once']));
    name.value = '';
    isSaving.value = false;
  }
});

/* --------------------------------- METHODS -------------------------------- */
// just dismisses the dialog — the user stays on the page, no skip logic runs
function close() {
  emit('close');
  dialogShow.value = false;
}

function setAllSelected(value: boolean) {
  const choice: PersistChoice = value ? 'all' : 'once';
  choices.value = Object.fromEntries(persistableItems.value.map((item) => [item.key, choice]));
}

function skip() {
  emit('skip');
  dialogShow.value = false;
}

function save() {
  const selectedKeys = Object.keys(choices.value).filter((key) => choices.value[key] === 'all');
  if (selectedKeys.length === 0) {
    skip();
    return;
  }

  const values: Record<string, unknown> = {};
  for (const key of selectedKeys) {
    values[key] = props.values[key];
  }

  isSaving.value = true;
  // naming is optional — fall back to the suggested name shown in the placeholder
  emit('save', { name: name.value.trim() || props.defaultName, values }, () => {
    isSaving.value = false;
  });
}
</script>

<style scoped>
.modal-content {
  position: relative;
}

.modal-close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--tblr-secondary, #667085);
}

.adjustment-option-list-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.adjustment-option-row {
  padding: 12px 0;
  border-bottom: 1px solid var(--tblr-border-color, #eaecf0);
}

.adjustment-option-row:last-child {
  border-bottom: none;
}

.adjustment-option-info {
  min-width: 0;
  margin-bottom: 8px;
}

.adjustment-option-label {
  font-weight: 600;
  color: #101828;
}

.adjustment-option-value {
  font-size: 0.8125rem;
  margin-top: 2px;
}

/* stretch the two choices to fill the row */
.adjustment-option-toggle :deep(.btn-group) {
  width: 100%;
}

.adjustment-option-toggle :deep(.btn) {
  flex: 1;
}
</style>
